<template>
  <AdminAppLayout title="HR-DOS | Leave Balances Dashboard">
    
    <!-- SEARCH BAR & TABS NAVIGATION -->
    <div class="row" style="margin-bottom: 20px;">
      <div class="col s12">
        <ul class="tabs z-depth-1" style="border-radius: 4px;">
          <li class="tab col s6"><a class="active" href="#pending-requests">Pending Requests</a></li>
          <li class="tab col s6"><a href="#all-balances">Employee Leave Balances</a></li>
        </ul>
      </div>
      <div class="col s12">
        <div class="input-field" style="margin-top: 0; margin-bottom: 0;">
          <i class="material-icons prefix" style="color: #9e9e9e; margin-top: 5px;">search</i>
          <input id="search" type="text" v-model="searchQuery" placeholder="Search by LTO ID or Name...">
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- TAB 1: PENDING BALANCE SETUP REQUESTS      -->
    <!-- ========================================== -->
    <div id="pending-requests">
      <div class="row" style="margin-bottom: 20px; margin-top: 20px;">
        <div class="col s12">
          <h5 style="font-weight: 600; margin: 10px 0 0 0; color: #333;">Pending Leave Balance Setup Requests</h5>
        </div>
      </div>

      <!-- SKELETON LOADER TABLE (DESKTOP) -->
      <div class="table-responsive-wrapper hide-on-med-and-down" v-show="loadingRequests">
        <table class="highlight">
          <thead>
            <tr>
              <th style="padding-left: 20px;">LTO ID</th>
              <th>Employee Name</th>
              <th>Office / Position</th>
              <th>Date Requested</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in 3" :key="'skeleton-row-' + n">
              <td style="padding-left: 20px;"><div class="skeleton skeleton-text" style="width: 80px; height: 16px;"></div></td>
              <td><div class="skeleton skeleton-text" style="width: 160px; height: 16px;"></div></td>
              <td>
                <div class="skeleton skeleton-text" style="width: 140px; height: 14px; margin-bottom: 6px;"></div>
                <div class="skeleton skeleton-text" style="width: 90px; height: 10px;"></div>
              </td>
              <td><div class="skeleton skeleton-text" style="width: 100px; height: 14px;"></div></td>
              <td><div class="skeleton skeleton-button" style="width: 90px; height: 32px; border-radius: 4px;"></div></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ACTUAL DATA TABLE (DESKTOP) -->
      <div class="table-responsive-wrapper hide-on-med-and-down" v-show="!loadingRequests">
        <table class="highlight responsive-table" style="border: 1px solid #e0e0e0; border-radius: 4px;">
          <thead class="grey lighten-4">
            <tr>
              <th style="padding-left: 20px;">LTO ID</th>
              <th>Employee Name</th>
              <th>Office / Position</th>
              <th>Date Requested</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredRequests.length === 0">
              <td colspan="5" class="center-align grey-text" style="padding: 30px;">No pending requests found.</td>
            </tr>
            
            <tr v-for="req in filteredRequests" :key="req.id">
              <td style="padding-left: 20px;" class="grey-text text-darken-1">{{ req.employee.employee_id_number || 'N/A' }}</td>
              <td class="bold">
                {{ req.employee.first_name }} {{ req.employee.last_name }}
              </td>
              <td class="grey-text text-darken-2">
                {{ req.employee.position }}<br>
                <small>{{ req.employee.office }}</small>
              </td>
              <td>{{ formatDate(req.created_at) }}</td>
              <td>
                <button v-if="req.status == 'Pending'" @click="openIssueModal(req)" class="btn-small blue darken-2 waves-light" style="border-radius: 4px;">
                  Issue Credits
                </button>
                <button v-else class="btn-small darken-2" style="border-radius: 4px; color: black; background-color: white; font-weight: bold; border: 1px solid #e0e0e0;">
                  Processed
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- SKELETON LOADER CARDS (MOBILE) -->
      <div class="mobile-card-grid hide-on-large-only left-align" v-show="loadingRequests">
        <div v-for="n in 3" :key="'skeleton-mob-req-' + n" class="grid-item">
          <div class="card z-depth-1" style="border-radius: 8px;">
            <div class="card-content" style="padding: 16px;">
              <div class="skeleton skeleton-text right" style="width: 70px; height: 18px; border-radius: 4px;"></div>
              <div class="skeleton skeleton-text" style="width: 60%; height: 20px; margin-bottom: 8px;"></div>
              <div class="skeleton skeleton-text" style="width: 75%; height: 14px; margin-bottom: 4px;"></div>
              <div class="skeleton skeleton-text" style="width: 50%; height: 12px; margin-bottom: 12px;"></div>
              <div class="skeleton skeleton-text" style="width: 140px; height: 12px; margin-bottom: 0;"></div>
            </div>
            <div class="card-action" style="padding: 10px 16px; background-color: #fafafa; border-radius: 0 0 8px 8px;">
              <div class="skeleton skeleton-button" style="width: 105px; height: 32px; border-radius: 4px;"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- ACTUAL DATA CARDS (MOBILE) -->
      <div class="mobile-card-grid hide-on-large-only left-align" v-show="!loadingRequests">
        <div v-if="filteredRequests.length === 0" class="center-align grey-text empty-state-message">
          No pending requests found.
        </div>
        <div v-for="req in filteredRequests" :key="'mob-' + req.id" class="grid-item">
          <div class="card z-depth-1" style="border-radius: 8px;">
            <div class="card-content" style="padding: 16px;">
              <span class="badge grey lighten-3 grey-text text-darken-2 right" style="border-radius: 4px; font-weight: bold; font-size: 0.75rem;">
                ID: {{ req.employee.employee_id_number || 'N/A' }}
              </span>
              <p style="font-size: 1.1rem; font-weight: bold; margin-bottom: 5px;">
                {{ req.employee.first_name }} {{ req.employee.last_name }}
              </p>
              <p class="grey-text text-darken-2" style="font-size: 0.9rem; margin-bottom: 10px;">
                {{ req.employee.position }}<br>
                <small>{{ req.employee.office }}</small>
              </p>
              <p class="grey-text" style="font-size: 0.8rem; margin-bottom: 0;">
                Requested: {{ formatDate(req.created_at) }}
              </p>
            </div>
            <div class="card-action" style="padding: 10px 16px; background-color: #fafafa; border-radius: 0 0 8px 8px;">
              <button v-if="req.status == 'Pending'" @click="openIssueModal(req)" class="btn-small blue darken-2 waves-light" style="border-radius: 4px;">
                Issue Credits
              </button>
              <button v-else class="btn-small darken-2" style="border-radius: 4px; color: black; background-color: white; font-weight: bold; border: 1px solid #e0e0e0;">
                Processed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- TAB 2: ALL EMPLOYEE LEAVE BALANCES         -->
    <!-- ========================================== -->
    <div id="all-balances">
      <div class="row" style="margin-bottom: 20px; margin-top: 20px;">
        <div class="col s12">
          <h5 style="font-weight: 600; margin: 10px 0 0 0; color: #333;">All Employee Leave Balances</h5>
        </div>
      </div>

      <!-- SKELETON LOADER TABLE (DESKTOP) -->
      <div class="table-responsive-wrapper hide-on-med-and-down" v-show="loadingBalances">
        <table class="highlight">
          <thead>
            <tr>
              <th style="padding-left: 20px;">LTO ID</th>
              <th>Employee Name</th>
              <th>Office / Position</th>
              <th>Vacation Leave Credits</th>
              <th>Sick Leave Credits</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="n in 3" :key="'skeleton-bal-row-' + n">
              <td style="padding-left: 20px;"><div class="skeleton skeleton-text" style="width: 80px; height: 16px;"></div></td>
              <td><div class="skeleton skeleton-text" style="width: 160px; height: 16px;"></div></td>
              <td><div class="skeleton skeleton-text" style="width: 140px; height: 14px;"></div></td>
              <td><div class="skeleton skeleton-text" style="width: 40px; height: 14px;"></div></td>
              <td><div class="skeleton skeleton-text" style="width: 40px; height: 14px;"></div></td>
              <td><div class="skeleton skeleton-button" style="width: 90px; height: 32px; border-radius: 4px;"></div></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ACTUAL DATA TABLE (DESKTOP) -->
      <div class="table-responsive-wrapper hide-on-med-and-down" v-show="!loadingBalances">
        <table class="highlight responsive-table" style="border: 1px solid #e0e0e0; border-radius: 4px;">
          <thead class="grey lighten-4">
            <tr>
              <th style="padding-left: 20px;">LTO ID</th>
              <th>Employee Name</th>
              <th>Office / Position</th>
              <th>Vacation Leave Credits</th>
              <th>Sick Leave Credits</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredBalances.length === 0">
              <td colspan="6" class="center-align grey-text" style="padding: 30px;">No leave balances found.</td>
            </tr>
            
            <tr v-for="balance in filteredBalances" :key="'bal-' + balance.id">
              <td style="padding-left: 20px;" class="grey-text text-darken-1">{{ balance.employee.employee_id_number || 'N/A' }}</td>
              <td class="bold">
                {{ balance.employee.first_name }} {{ balance.employee.last_name }}
              </td>
              <td class="grey-text text-darken-2">
                {{ balance.employee.position }}<br>
                <small>{{ balance.employee.office }}</small>
              </td>
              <td>{{ Number(balance.vacation_leave_credits).toFixed(3) }}</td>
              <td>{{ Number(balance.sick_leave_credits).toFixed(3) }}</td>
              <td>
                <button @click="openAddCreditsModal(balance)" class="btn-small green darken-2 waves-light" style="border-radius: 4px;">
                  Add Credits
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- SKELETON LOADER CARDS (MOBILE) -->
      <div class="mobile-card-grid hide-on-large-only left-align" v-show="loadingBalances">
        <div v-for="n in 3" :key="'skeleton-mob-bal-' + n" class="grid-item">
          <div class="card z-depth-1" style="border-radius: 8px;">
            <div class="card-content" style="padding: 16px;">
              <div class="skeleton skeleton-text right" style="width: 70px; height: 18px; border-radius: 4px;"></div>
              <div class="skeleton skeleton-text" style="width: 60%; height: 20px; margin-bottom: 8px;"></div>
              <div class="skeleton skeleton-text" style="width: 75%; height: 14px; margin-bottom: 4px;"></div>
              <div class="skeleton skeleton-text" style="width: 50%; height: 12px; margin-bottom: 18px;"></div>
              <div class="row" style="margin-bottom: 0;">
                <div class="col s6">
                  <div class="skeleton skeleton-text" style="width: 25px; height: 12px; margin-bottom: 6px;"></div>
                  <div class="skeleton skeleton-text" style="width: 50px; height: 18px;"></div>
                </div>
                <div class="col s6">
                  <div class="skeleton skeleton-text" style="width: 25px; height: 12px; margin-bottom: 6px;"></div>
                  <div class="skeleton skeleton-text" style="width: 50px; height: 18px;"></div>
                </div>
              </div>
            </div>
            <div class="card-action" style="padding: 10px 16px; background-color: #fafafa; border-radius: 0 0 8px 8px;">
              <div class="skeleton skeleton-button" style="width: 95px; height: 32px; border-radius: 4px;"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ACTUAL DATA CARDS (MOBILE) -->
      <div class="mobile-card-grid hide-on-large-only left-align" v-show="!loadingBalances">
        <div v-if="filteredBalances.length === 0" class="center-align grey-text empty-state-message">
          No leave balances found.
        </div>
        <div v-for="balance in filteredBalances" :key="'mob-bal-' + balance.id" class="grid-item">
          <div class="card z-depth-1" style="border-radius: 8px;">
            <div class="card-content" style="padding: 16px;">
              <span class="badge grey lighten-3 grey-text text-darken-2 right" style="border-radius: 4px; font-weight: bold; font-size: 0.75rem;">
                ID: {{ balance.employee.employee_id_number || 'N/A' }}
              </span>
              <p style="font-size: 1.1rem; font-weight: bold; margin-bottom: 5px; max-width: 75%;">
                {{ balance.employee.first_name }} {{ balance.employee.last_name }}
              </p>
              <p class="grey-text text-darken-2" style="font-size: 0.9rem; margin-bottom: 15px;">
                {{ balance.employee.position }}<br>
                <small>{{ balance.employee.office }}</small>
              </p>
              <div class="row" style="margin-bottom: 0;">
                <div class="col s6">
                  <span class="grey-text" style="font-size: 0.8rem;">Vacation Leave Credits</span><br>
                  <span class="bold" style="font-size: 1.1rem;">{{ Number(balance.vacation_leave_credits).toFixed(3) }}</span>
                </div>
                <div class="col s6">
                  <span class="grey-text" style="font-size: 0.8rem;">Sick Leave Credits</span><br>
                  <span class="bold" style="font-size: 1.1rem;">{{ Number(balance.sick_leave_credits).toFixed(3) }}</span>
                </div>
              </div>
            </div>
            <div class="card-action" style="padding: 10px 16px; background-color: #fafafa; border-radius: 0 0 8px 8px;">
              <button @click="openAddCreditsModal(balance)" class="btn-small green darken-2 waves-light" style="border-radius: 4px;">
                Add Credits
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- ISSUE CREDITS MODAL (For Setup Requests)   -->
    <!-- ========================================== -->
    <div id="issueCreditsModal" class="modal centered-modal main-modal" style="width: 90%; max-width: 500px; border-radius: 8px; height: auto !important; max-height: 90% !important;">
      <div class="modal-content" style="padding: 24px;">
        <h5 style="margin-top: 0; font-weight: bold;">Issue Leave Credits</h5>
        
        <p class="grey-text text-darken-2" v-if="selectedRequest">
          Set the initial leave balances for <strong>{{ selectedRequest.employee.first_name }} {{ selectedRequest.employee.last_name }}</strong>.
        </p>

        <div class="row" style="margin-top: 25px; margin-bottom: 0;">
          <div class="input-field col s12 m6">
            <input id="vacation_credits" type="number" step="0.001" min="0" v-model="vacationCredits">
            <label for="vacation_credits" class="active">Vacation Leave Credits</label>
          </div>
          <div class="input-field col s12 m6">
            <input id="sick_credits" type="number" step="0.001" min="0" v-model="sickCredits">
            <label for="sick_credits" class="active">Sick Leave Credits</label>
          </div>
        </div>
      </div>
      <div class="modal-footer grey lighten-3 custom-footer" style="padding-bottom: 15px; padding-right: 20px;">
        <button class="btn-flat modal-close waves-effect grey-text text-darken-2 left" :disabled="isIssuing" style="font-weight: bold;">Cancel</button>
        <button class="btn blue darken-2 waves-light right" @click="submitCredits" :disabled="isIssuing" style="border-radius: 6px;">
          <span v-if="isIssuing">Saving...</span>
          <span v-else>Save Credits</span>
        </button>
      </div>
    </div>

    <!-- ========================================== -->
    <!-- ADD CREDITS MODAL                          -->
    <!-- ========================================== -->
    <div id="addCreditsModal" class="modal centered-modal main-modal" style="width: 90%; max-width: 500px; border-radius: 8px; height: auto !important; max-height: 90% !important;">
      <div class="modal-content" style="padding: 24px;">
        
        <h6 class="grey-text text-darken-2" v-if="selectedBalance">
          Add extra credits for <br> <strong style="margin-top: 0; font-weight: bold;">{{ selectedBalance.employee.first_name }} {{ selectedBalance.employee.last_name }}</strong>.
        </h6>

        <div class="row" style="margin-top: 25px; margin-bottom: 0;">
          <div class="input-field col s12 m6">
            <input id="add_vacation_credits" type="number" step="0.001" min="0" v-model="addVacationCredits">
            <label for="add_vacation_credits" class="active">Add Vacation Leave Credits</label>
          </div>
          <div class="input-field col s12 m6">
            <input id="add_sick_credits" type="number" step="0.001" min="0" v-model="addSickCredits">
            <label for="add_sick_credits" class="active">Add Sick Leave Credits</label>
          </div>
        </div>

        <div class="row" style="margin-bottom: 5px;">
          <div class="col s12">
            <span style="color: black; font-size: 0.9rem;">Reason for adding credits</span>
            
            <p>
              <label style="color: black;">
                <input name="add_reason_radio" type="radio" value="Overtime pay" v-model="selectedReason" />
                <span>Overtime pay</span>
              </label>
            </p>
            <p>
              <label style="color: black;">
                <input name="add_reason_radio" type="radio" value="Other" v-model="selectedReason" />
                <span>Other</span>
              </label>
            </p>
          </div>
        </div>

        <div class="row" v-if="selectedReason === 'Other'" style="margin-bottom: 5px;">
          <div class="input-field col s12">
            <input id="custom_reason" type="text" v-model="customReason" required>
            <label for="custom_reason">Please specify the reason</label>
          </div>
        </div>

        <p class="grey-text" style="font-size: 0.85rem; margin-top: 0;">
          * These values will be <strong>added</strong> to their current balance.
        </p>
      </div>
      <div class="modal-footer grey lighten-3 custom-footer" style="padding-bottom: 15px; padding-right: 20px;">
        <button class="btn-flat modal-close waves-effect grey-text text-darken-2 left" :disabled="isAdding" style="font-weight: bold;">Cancel</button>
        <button class="btn green darken-2 waves-light right" @click="submitAddCredits" :disabled="isAdding" style="border-radius: 6px;">
          <span v-if="isAdding">Adding...</span>
          <span v-else>Confirm & Add</span>
        </button>
      </div>
    </div>

  </AdminAppLayout>
</template>

<style scoped>
/* Tabs Styling Customization */
.tabs .tab a {
  color: #5c6bc0; /* Indigo lighten-1 */
  font-weight: bold;
}
.tabs .tab a:hover, .tabs .tab a.active {
  color: #283593; /* Indigo darken-3 */
}
.tabs .indicator {
  background-color: #283593;
}

/* ========================================== */
/* CSS GRID FIX FOR MOBILE CARDS              */
/* ========================================== */
.mobile-card-grid {
  display: grid;
  grid-template-columns: 1fr; /* Single column on small screens */
  gap: 16px; /* Spacing between cards */
  margin-bottom: 20px;
}

/* Switch to 2 columns on tablet sizes (mimicking m6) */
@media only screen and (min-width: 601px) {
  .mobile-card-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Ensure empty state message spans full width */
.empty-state-message {
  grid-column: 1 / -1;
  padding: 30px;
}

.grid-item {
  display: flex;
  flex-direction: column;
}

.grid-item .card {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0; /* Override materialize default card margin */
}

.grid-item .card-content {
  flex: 1 1 auto; /* Pushes the card-action button to the bottom */
}

/* Tables */
.table-responsive-wrapper {
  overflow-x: auto;
  width: 100%;
  margin-bottom: 20px;
}
.table-responsive-wrapper table {
  min-width: 800px;
}
tbody tr td {
  padding: 12px 10px;
  font-size: 1rem;
  vertical-align: middle;
}
thead th {
  font-size: 1.1rem;
  font-weight: bold;
}

/* Modals */
.custom-footer {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 0 24px !important;
  height: 65px !important; 
}
.centered-modal {
  top: 50% !important;
  transform: translateY(-50%) !important;
}
</style>

<script>
import api from '@/axios.js';
import AdminAppLayout from '@/views/layout/AdminAppLayout.vue';

export default {
  name: "LeaveBalancesView",
  components: { AdminAppLayout },
  data() {
    return {
      searchQuery: '', // New search state
      
      // Pending Requests State
      loadingRequests: true,
      requests: [],
      
      // All Balances State
      loadingBalances: true,
      balances: [],
      
      // Issuance Modal State (Setup)
      selectedRequest: null,
      vacationCredits: 0,
      sickCredits: 0,
      isIssuing: false,

      // Add Credits Modal State (Overtime/Adjustments)
      selectedBalance: null,
      addVacationCredits: 0,
      addSickCredits: 0,
      selectedReason: '', // Tracks the radio selection
      customReason: '',   // Tracks the custom text input
      isAdding: false
    };
  },
  computed: {
    // Computes the final reason to send to the backend based on radio choice
    finalAddReason() {
      if (this.selectedReason === 'Other') {
        return this.customReason;
      }
      return this.selectedReason;
    },

    filteredRequests() {
      // 1. Filter by Search Query
      const query = this.searchQuery.toLowerCase();
      const filtered = this.requests.filter(req => {
        const fullName = `${req.employee.first_name} ${req.employee.last_name}`.toLowerCase();
        const ltoId = (req.employee.employee_id_number || '').toLowerCase();
        return fullName.includes(query) || ltoId.includes(query);
      });

      // 2. Sort the filtered results (Pending first)
      return filtered.sort((a, b) => {
        if (a.status === 'Pending' && b.status !== 'Pending') return -1; 
        if (a.status !== 'Pending' && b.status === 'Pending') return 1;  
        return 0; 
      });
    },

    filteredBalances() {
      // Filter by Search Query
      const query = this.searchQuery.toLowerCase();
      return this.balances.filter(balance => {
        const fullName = `${balance.employee.first_name} ${balance.employee.last_name}`.toLowerCase();
        const ltoId = (balance.employee.employee_id_number || '').toLowerCase();
        return fullName.includes(query) || ltoId.includes(query);
      });
    }
  },
  methods: {
    checkAndOpenModal() {
      const urlParams = new URLSearchParams(window.location.search);
      const reqId = this.$route?.query?.req || urlParams.get('req');

      if (reqId) {
        // Find the specific request object matching the ID from the URL
        const targetReq = this.requests.find(r => r.id == reqId);
        
        if (targetReq) {
          this.$nextTick(() => {
            // A tiny delay ensures the DOM is fully rendered with the newly fetched data
            setTimeout(() => {
              this.openIssueModal(targetReq);
            }, 300);
          });
        }
      }
    },

    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        
        return date.toLocaleDateString('en-US', {
            month: 'long',  
            day: '2-digit', 
            year: 'numeric' 
        });
    },

    fetchRequests() {
      this.loadingRequests = true;
      api.get('/leave-balances/leave-balance-requests')
        .then(response => {
          this.requests = response.data.requests;

          this.checkAndOpenModal();
        })
        .catch(error => {
          console.error("Error fetching requests:", error);
          if (window.M) window.M.toast({ html: 'Failed to load requests.', classes: 'red' });
        })
        .finally(() => {
          this.loadingRequests = false;
        });
    },

    fetchBalances() {
      this.loadingBalances = true;
      api.get('/all-leave-balances') 
        .then(response => {
          this.balances = response.data.leave_balances;
        })
        .catch(error => {
          console.error("Error fetching balances:", error);
          if (window.M) window.M.toast({ html: 'Failed to load leave balances.', classes: 'red' });
        })
        .finally(() => {
          this.loadingBalances = false;
        });
    },

    // ---------- ISSUE SETUP CREDITS METHODS ---------- //
    openIssueModal(req) {
      this.selectedRequest = req;
      this.vacationCredits = 0; 
      this.sickCredits = 0; 

      if (window.M) {
        const modal = document.getElementById('issueCreditsModal');
        const instance = window.M.Modal.getInstance(modal) || window.M.Modal.init(modal);
        instance.open();
        window.M.updateTextFields();
      }
    },

    submitCredits() {
      if (!this.selectedRequest) return;
      
      this.isIssuing = true;

      api.post('/admin/leave-balances/issue', {
        employee_id: this.selectedRequest.employee_id,
        vacation_leave_credits: this.vacationCredits,
        sick_leave_credits: this.sickCredits
      })
      .then(response => {
        if (window.M) {
          window.M.toast({ html: response.data.message, classes: 'green' });
        }
        
        const modal = document.getElementById('issueCreditsModal');
        const instance = window.M.Modal.getInstance(modal);
        if (instance) instance.close();
        
        this.fetchRequests();
        this.fetchBalances();
      })
      .catch(error => {
        console.error("Error issuing credits:", error);
        const errorMsg = error.response?.data?.error || "Failed to issue credits.";
        if (window.M) {
          window.M.toast({ html: errorMsg, classes: 'red' });
        }
      })
      .finally(() => {
        this.isIssuing = false;
      });
    },

    // ---------- ADD OVERTIME CREDITS METHODS ---------- //
    openAddCreditsModal(balance) {
      this.selectedBalance = balance;
      this.addVacationCredits = 0; 
      this.addSickCredits = 0; 
      this.selectedReason = ''; // Reset radio choice
      this.customReason = '';   // Reset text input

      if (window.M) {
        const modal = document.getElementById('addCreditsModal');
        const instance = window.M.Modal.getInstance(modal) || window.M.Modal.init(modal);
        instance.open();
        window.M.updateTextFields();
      }
    },

    submitAddCredits() {
      if (!this.selectedBalance) return;
      
      // Grab the computed reason
      const reasonToSubmit = this.finalAddReason;

      if (!reasonToSubmit || reasonToSubmit.trim() === '') {
        if (window.M) window.M.toast({ html: 'Please provide a reason for adding credits.', classes: 'red' });
        return;
      }

      this.isAdding = true;

      api.post('/admin/leave-balances/add-credits', {
        employee_id: this.selectedBalance.employee_id,
        added_vacation_credits: this.addVacationCredits,
        added_sick_credits: this.addSickCredits,
        reason: reasonToSubmit // Pass the computed reason
      })
      .then(response => {
        if (window.M) {
          window.M.toast({ html: response.data.message || 'Credits added successfully.', classes: 'green' });
        }
        
        const modal = document.getElementById('addCreditsModal');
        const instance = window.M.Modal.getInstance(modal);
        if (instance) instance.close();
        
        this.fetchBalances();
      })
      .catch(error => {
        console.error("Error adding credits:", error);
        const errorMsg = error.response?.data?.error || "Failed to add credits.";
        if (window.M) {
          window.M.toast({ html: errorMsg, classes: 'red' });
        }
      })
      .finally(() => {
        this.isAdding = false;
      });
    }
  },
  mounted() {
    this.fetchRequests();
    this.fetchBalances(); 
    
    this.$nextTick(() => {
      if (window.M) {
        const modals = document.querySelectorAll('.modal');
        window.M.Modal.init(modals);
        
        const tabs = document.querySelectorAll('.tabs');
        window.M.Tabs.init(tabs);
      }
    });
  }
};
</script>